import React, { useEffect, useState } from "react";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useSnackbar } from 'notistack';
import httpRequest from "../../httpRequest";

const TemplateEditor = ({ templateId }) => {
  const [templateData, setTemplateData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(""); // "add", "rename", "delete"
  const [selectedField, setSelectedField] = useState(null); // Track which field is selected for editing or deleting
  const [fieldData, setFieldData] = useState({ topLevelField: "", itemsField: "", oldField: "", newField: "" });
  const [fieldOrder, setFieldOrder] = useState({ topLevel: [], items: [] });
  const { enqueueSnackbar } = useSnackbar();

  const lockedTopLevelFields = [
    "Invoice Date", "Document Number", "Invoice Amount", "Tax Amount", "Due Date",
    "Currency", "Payment Terms", "Purchase Order Number", "Header Text", "Vendor Name"
  ];
  
  const lockedItemFields = [
    "Item Number", "Material Number", "Material Description", "Delivery Note Number",
    "Quantity", "Unit of Measure", "Price", "Total Amount", "Tax Amount", "Net Amount"
  ];
  

  // Fetch template data based on templateId prop
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!templateId) return;
      try {
        const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}`);
        const data = JSON.parse(resp.data.template_data);
        setTemplateData(data);
        setFieldOrder({
          topLevel: Object.keys(data).filter(key => key !== "Items"),
          items: data.Items.length > 0 ? Object.keys(data.Items[0]) : []
        });
      } catch (error) {
        console.error("Error fetching template:", error);
        enqueueSnackbar("Error fetching template data", { variant: "error" });
      }
    };
    fetchTemplate();
  }, [templateId]);
  
  const handleDialogOpen = (type, field) => {
    setDialogType(type);
    setSelectedField(field);
    setDialogOpen(true);
  
    if (type === "rename") {
      setFieldData({ oldField: field, newField: "" });
    } else if (type === "delete") {
      setFieldData({ topLevelField: field, itemsField: "" });
    } else if (type === "add") {
      // Set up field data for adding, distinguishing between top level and items
      if (field === "topLevel") {
        setFieldData({ topLevelField: "", itemsField: "" });
      } else {
        setFieldData({ topLevelField: "", itemsField: "" });
      }
    }
  };
  

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFieldData({ topLevelField: "", itemsField: "", oldField: "", newField: "" });
    setSelectedField(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFieldData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const isValidFieldName = (name) => /^[a-zA-Z0-9 ]*$/.test(name);




  const handleAddField = async () => {
    // Trim and validate field names
  const newTopLevelField = fieldData.topLevelField.trim();
  const newItemsField = fieldData.itemsField.trim();

  if (!isValidFieldName(newTopLevelField) || !isValidFieldName(newItemsField)) {
    enqueueSnackbar("Field names can only contain letters, numbers, and spaces.", { variant: 'error' });
    return;
  }

    // Check for duplicates across both top-level and item fields
  const allFields = [...fieldOrder.topLevel, ...fieldOrder.items];
  if (allFields.includes(newTopLevelField) || allFields.includes(newItemsField)) {
    enqueueSnackbar("Field name already exists in top-level or item fields.", { variant: 'error' });
    return;
  }

    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/template/add_field`, {
        top_level_field: fieldData.topLevelField,
        items_field: fieldData.itemsField,
        template_id: templateId,
      });
      setDialogOpen(false);
      // Reload template data
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}`);
      const data = JSON.parse(resp.data.template_data);
      setTemplateData(data);
      setFieldOrder({
        topLevel: Object.keys(data).filter(key => key !== "Items"),
        items: data.Items.length > 0 ? Object.keys(data.Items[0]) : []
      });
      enqueueSnackbar("Field added successfully.", { variant: 'success' });
    } catch (error) {
      console.error("Error adding field:", error);
      enqueueSnackbar("Error adding field. Please try again.", { variant: 'error' });
    }
  };

  const handleRenameField = async () => {
    // Trim and validate field names
  const oldField = fieldData.oldField.trim();
  const newField = fieldData.newField.trim();

  if (!isValidFieldName(newField)) {
    enqueueSnackbar("Field names can only contain letters, numbers, and spaces.", { variant: 'error' });
    return;
  }

  // Ensure new field name is different and not a duplicate
  if (oldField === newField) {
    enqueueSnackbar("New field name must be different from the old name.", { variant: 'error' });
    return;
  }

  const allFields = [...fieldOrder.topLevel, ...fieldOrder.items];
  if (allFields.includes(newField)) {
    enqueueSnackbar("Field name already exists in top-level or item fields.", { variant: 'error' });
    return;
  }

    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}/rename_field`, {
        old_top_level_field: fieldData.oldField,
        new_top_level_field: fieldData.newField,
        old_items_field: fieldData.oldField,
        new_items_field: fieldData.newField,
      });
      setDialogOpen(false);
      // Reload template data
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}`);
      const data = JSON.parse(resp.data.template_data);
      setTemplateData(data);
      setFieldOrder({
        topLevel: Object.keys(data).filter(key => key !== "Items"),
        items: data.Items.length > 0 ? Object.keys(data.Items[0]) : []
      });
      enqueueSnackbar("Field renamed successfully.", { variant: 'success' });
    } catch (error) {
      console.error("Error renaming field:", error);
      enqueueSnackbar("Error renaming field. Please try again.", { variant: 'error' });
    }
  };

  const handleDeleteField = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}/delete_field`, {
        data: {
          top_level_field: fieldData.topLevelField || selectedField,
          items_field: fieldData.itemsField || selectedField,
        }
      });
      setDialogOpen(false);
      // Reload template data
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}`);
      const data = JSON.parse(resp.data.template_data);
      setTemplateData(data);
      setFieldOrder({
        topLevel: Object.keys(data).filter(key => key !== "Items"),
        items: data.Items.length > 0 ? Object.keys(data.Items[0]) : []
      });
    } catch (error) {
      console.error("Error deleting field:", error);
    }
  };

  const handleRearrangeFields = async (type, order) => {
    try {
      console.log(`Rearranging ${type} fields with order:`, order);
      const response = await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}/rearrange_fields/${type}`, {
        field_order: order,
      });
      console.log('API Response:', response.data);
      // Reload template data
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/${templateId}`);
      const data = JSON.parse(resp.data.template_data);
      setTemplateData(data);
      setFieldOrder({
        topLevel: Object.keys(data).filter(key => key !== "Items"),
        items: data.Items.length > 0 ? Object.keys(data.Items[0]) : []
      });
    } catch (error) {
      console.error(`Error rearranging ${type} fields:`, error);
    }
  };
  
  

  const moveField = (type, index, direction) => {
    const newOrder = [...fieldOrder[type]];
    const [movedField] = newOrder.splice(index, 1);
  
    // If the moved field is "Vendor Name", don't update the field order
    if (movedField !== "Vendor Name") {
      let newIndex = index + direction;
  
      // Find the next available index to insert the moved field
      while (newOrder[newIndex] === "Vendor Name") {
        newIndex += direction;
      }
  
      newOrder.splice(newIndex, 0, movedField);
      setFieldOrder({ ...fieldOrder, [type]: newOrder });
      handleRearrangeFields(type, newOrder);
    }
  };
  
  

 
  const renderTable = (data, type) => (
    <TableContainer
      style={{
        border: '2px solid #354a5f',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
          <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              style={{
                backgroundColor: '#354a5f',
                color: 'white',
                fontFamily: '"SapFiori72", sans-serif',
              }}
            >
              {type === "topLevel" ? "Top-Level Fields" : "Item Fields"}
            </TableCell>
            <TableCell
              style={{
                backgroundColor: '#354a5f',
                color: 'white',
                fontFamily: '"SapFiori72", sans-serif',
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((field, index) => {
             if (type === "topLevel" && field === "Vendor Name") {
                return null;
              }
              return (
            <TableRow key={field}>
              <TableCell>{field}</TableCell>
              <TableCell>
                {type === "topLevel" ? (
                  <>
                    <IconButton onClick={() => moveField('topLevel', index, -1)} disabled={index === 0}>
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton onClick={() => moveField('topLevel', index, 1)} disabled={index === data.length - 1}>
                      <ArrowDownwardIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton onClick={() => moveField('items', index, -1)} disabled={index === 0}>
                      <ArrowLeftIcon />
                    </IconButton>
                    <IconButton onClick={() => moveField('items', index, 1)} disabled={index === data.length - 1}>
                      <ArrowRightIcon />
                    </IconButton>
                  </>
                )}
                <IconButton onClick={() => handleDialogOpen("rename", field)} disabled={lockedTopLevelFields.includes(field)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDialogOpen("delete", field)} disabled={lockedTopLevelFields.includes(field)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
           );
        })}
          <TableRow>
            <TableCell colSpan={2}>
              <IconButton onClick={() => handleDialogOpen("add", "topLevel")}>
                <AddIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
  
  return (
    <div style={{ margin: '20px' }}>
      {templateData && (
        <>
          {renderTable(fieldOrder.topLevel, "topLevel")}
         

          <TableContainer
            style={{
              border: '2px solid #354a5f',
              borderRadius: '8px',
              overflow: 'hidden',
              marginTop: '20px'
            }}
          >
             <div style={{ overflowX: 'auto' }}>
    <Table size="small">
                      <TableHead>
                <TableRow>
                  {fieldOrder.items.map((field) => (
                    <TableCell
                      key={field}
                      align="center"
                      style={{
                        backgroundColor: '#354a5f',
                        color: 'white',
                        fontFamily: '"SapFiori72", sans-serif',
                      }}
                    >
                      {field}
                    </TableCell>
                  ))}
                  <TableCell
                    style={{
                      backgroundColor: '#354a5f',
                      color: 'white',
                      fontFamily: '"SapFiori72", sans-serif',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {fieldOrder.items.map((field, index) => (
                    <TableCell key={field} align="center">
                      <IconButton onClick={() => moveField('items', index, -1)} disabled={index === 0}>
                        <ArrowLeftIcon />
                      </IconButton>
                      <IconButton onClick={() => moveField('items', index, 1)} disabled={index === fieldOrder.items.length - 1}>
                        <ArrowRightIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDialogOpen("rename", field)} disabled={lockedItemFields.includes(field)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDialogOpen("delete", field)} disabled={lockedItemFields.includes(field)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton onClick={() => handleDialogOpen("add", "items")}>
                      <AddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </div>
          </TableContainer>
         
        </>
      )}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{dialogType === "add" ? "Add Field" : dialogType === "rename" ? "Rename Field" : "Delete Field"}</DialogTitle>
        <DialogContent>
  {dialogType === "add" && (
    <>
      {selectedField === "topLevel" ? (
        <TextField
          name="topLevelField"
          label="New Top-Level Field"
          value={fieldData.topLevelField}
          onChange={handleFieldChange}
          fullWidth
          error={!isValidFieldName(fieldData.topLevelField)}
          helperText={
            !isValidFieldName(fieldData.topLevelField) ? "Invalid field name. Only letters, numbers, and spaces are allowed." : ""
          }
        />
      ) : (
        <TextField
          name="itemsField"
          label="New Items Field"
          value={fieldData.itemsField}
          onChange={handleFieldChange}
          fullWidth
          error={!isValidFieldName(fieldData.itemsField)}
          helperText={
            !isValidFieldName(fieldData.itemsField) ? "Invalid field name. Only letters, numbers, and spaces are allowed." : ""
          }
        />
      )}
    </>
  )}
  {dialogType === "rename" && (
    <>
      <TextField
        name="oldField"
        label="Current Field Name"
        value={fieldData.oldField}
        onChange={handleFieldChange}
        fullWidth
        InputProps={{ readOnly: true }} // Make this read-only
        style={{ marginTop: '10px' }}
      />
      <TextField
        name="newField"
        label="New Field Name"
        value={fieldData.newField}
        onChange={handleFieldChange}
        fullWidth
        style={{ marginTop: '20px' }}

        error={!isValidFieldName(fieldData.newField)}
        helperText={
          !isValidFieldName(fieldData.newField) ? "Invalid field name. Only letters, numbers, and spaces are allowed." : ""
        }
      />
    </>
  )}
  {dialogType === "delete" && (
    <div>
      Are you sure you want to delete the field "{selectedField}"?
    </div>
  )}
</DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          {dialogType === "add" && <Button onClick={handleAddField}>Add</Button>}
          {dialogType === "rename" && <Button onClick={handleRenameField}>Rename</Button>}
          {dialogType === "delete" && <Button onClick={handleDeleteField}>Delete</Button>}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TemplateEditor;
